import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import useEffectOnce from "../hooks/useEffectOnce";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";

// --- Components ---
import ReportTable from "../components/ReportTable";
import Modal from "../components/Modal";
import Loader from "../components/Loader";
import { displayStatus, formatDateTimeDisplay } from "../utils/common";
import PortalDropdown from "../components/PortalDropdown";
import { getUpdateColors } from "../utils/badgeColors";
import PageHeader from "../components/PageHeader";

// --- Redux ---
import {
  createStatusTrackerApis,
  updateDevelopmentTasksApis,
} from "../redux/tasks/taskActions";
import {
  selectTestingLists,
  selectTestingListsStatus,
} from "../redux/testing/testingSelectors";
import { testingListsApis } from "../redux/testing/testingActions";

// --- Team routing ---
import MoveTaskModal from "../components/MoveTaskModal";
import TaskImages from "../components/TaskImages";
import TeamBadge from "../components/TeamBadge";
import { useTabRefresh } from "../context/TabsContext";

// --------------------------------------------------------------------------
// HELPER FUNCTIONS
// --------------------------------------------------------------------------




const formatDateForPayload = (dateInput) => {
  if (!dateInput) return null;
  return moment(dateInput).format("YYYY-MM-DDTHH:mm:ss");
};

const isValidText = (text) => {
  if (!text) return false;
  const trimmedText = text.trim();
  if (trimmedText.length < 10) return false;
  if (!/[a-zA-Z]/.test(trimmedText)) return false;
  if (!/[aeiouAEIOU]/.test(trimmedText)) return false;
  if (/(.)\1{4,}/.test(trimmedText)) return false;
  if (/[^\s]{15,}/.test(trimmedText)) return false;
  if (/[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]{6,}/.test(trimmedText))
    return false;
  if (/\b(\w+)\s+\1\s+\1\b/i.test(trimmedText)) return false;
  if (/^([a-zA-Z0-9]\s+){4,}[a-zA-Z0-9]$/.test(trimmedText)) return false;
  return true;
};

// ==========================================
// IS UPDATE SELECT DROPDOWN
// ==========================================
const IsUpdateSelect = ({ initialValue, onChange, readOnly = false }) => {
  const [value, setValue] = useState(initialValue || "None");
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => setValue(initialValue || "None"), [initialValue]);

  const getColorMap = getUpdateColors;
  const colors = getColorMap(value);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;

    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: "none",
          fontSize: "0.75rem",
          fontWeight: "600",
          padding: "6px 12px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: readOnly ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {value}{" "}
        {!readOnly && (
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>
        )}
      </button>

      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={buttonRef}
      >
        <div className="p-2">
          {["Backend work", "Update", "Not update"].map((option) => (
            <div
              key={option}
              onClick={(e) => {
                e.stopPropagation();
                setValue(option);
                onChange(option);
                setIsOpen(false);
              }}
              className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-1 dropdown-item-hover"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                backgroundColor: value === option ? "#f8f9fa" : "transparent",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  value === option ? "#f8f9fa" : "transparent")
              }
            >
              <i
                className="bi bi-circle-fill"
                style={{ fontSize: "0.5rem", color: getColorMap(option).text }}
              ></i>
              <span className="text-dark">{option}</span>
            </div>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

// ==========================================
// 3. MODERN CLIENT FILTER DROPDOWN (FIXED SCROLL)
// ==========================================
const ClientFilterDropdown = ({ value, options, onChange, count }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown if user clicks anywhere outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    // position-relative makes sure the absolute dropdown stays glued to this container
    <div className="position-relative" ref={wrapperRef}>
      <div
        onClick={handleToggle}
        className="d-flex align-items-center gap-2 bg-white px-3 py-1 rounded border shadow-sm"
        style={{
          cursor: "pointer",
          transition: "all 0.2s",
          userSelect: "none",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#f8f9fa")
        }
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
      >
        <span
          className="text-muted small fw-medium"
          style={{ fontSize: "0.8rem" }}
        >
          Tasks:
        </span>
        <div
          className="fw-bold text-primary d-flex align-items-center gap-1"
          style={{ fontSize: "0.85rem" }}
        >
          <span
            title={value === "All" ? "All Clients" : value}
            style={{
              display: "inline-block",
              maxWidth: "200px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              verticalAlign: "bottom",
            }}
          >
            {value === "All" ? "All Clients" : value}
          </span>
          <i
            className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
            style={{ fontSize: "0.7rem", marginLeft: "4px", marginTop: "2px" }}
          ></i>
        </div>
        <span
          className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
          style={{
            width: "24px",
            height: "24px",
            fontSize: "0.75rem",
            marginLeft: "4px",
          }}
        >
          {count}
        </span>
      </div>

      {/* Render natively in the DOM with position-absolute, so it scrolls perfectly with the page */}
      {isOpen && (
        <div
          className="position-absolute shadow-lg bg-white p-2"
          style={{
            top: "calc(100% + 8px)",
            right: 0, // Aligns the dropdown with the right edge of the button
            borderRadius: "8px",
            zIndex: 1050,
            border: "1px solid rgba(0,0,0,0.08)",
            maxHeight: "300px",
            overflowY: "auto",
            minWidth: "260px",
            maxWidth: "350px",
            animation: "fadeIn 0.1s ease-out",
          }}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              onChange("All");
              setIsOpen(false);
            }}
            className="d-flex align-items-center gap-2 rounded-2 px-3 py-2 mb-1"
            style={{
              cursor: "pointer",
              fontSize: "0.85rem",
              backgroundColor: value === "All" ? "#f0f7ff" : "transparent",
            }}
            onMouseEnter={(e) => {
              if (value !== "All")
                e.currentTarget.style.backgroundColor = "#f8f9fa";
            }}
            onMouseLeave={(e) => {
              if (value !== "All")
                e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <span
              className={value === "All" ? "fw-bold text-primary" : "text-dark"}
            >
              All Clients
            </span>
          </div>
          {options.map((client) => (
            <div
              key={client}
              title={client}
              onClick={(e) => {
                e.stopPropagation();
                onChange(client);
                setIsOpen(false);
              }}
              className="d-flex align-items-center gap-2 rounded-2 px-3 py-2 mb-1"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                backgroundColor: value === client ? "#f0f7ff" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (value !== client)
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
              }}
              onMouseLeave={(e) => {
                if (value !== client)
                  e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <span
                className={
                  value === client ? "fw-bold text-primary" : "text-dark"
                }
                style={{
                  display: "inline-block",
                  maxWidth: "280px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {client}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// UI COMPONENTS
// --------------------------------------------------------------------------
/* Status and "hand this to another team" share one control — same reasoning as
   the copy on Development Tasks. `canMove` is separate from `readOnly` so a
   task whose status this person may not set can still be passed on, which is
   what the Move column allowed. */
const StatusSelect = ({
  currentStatus,
  onChange,
  readOnly,
  onMove,
  canMove = false,
  moveHint,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);

  // Nothing to offer: no status to set and nowhere to send it.
  const isInert = readOnly && !canMove;

  const getColorParams = (status) => {
    switch (status) {
      case "Complete":
        return { bg: "#e8f5e9", text: "#2e7d32", dot: "#2e7d32" };
      case "Update":
        return { bg: "#e3f2fd", text: "#0d6efd", dot: "#0d6efd" };
      case "Testing In Progress":
        return { bg: "#e0f7fa", text: "#00838f", dot: "#00838f" };
      case "Testing On Hold":
        return { bg: "#fce4ec", text: "#c2185b", dot: "#c2185b" };
      case "Reassign":
        return { bg: "#fff8e1", text: "#f57c00", dot: "#f57c00" };
      default:
        return { bg: "#f8f9fa", text: "#6c757d", dot: "#adb5bd" };
    }
  };
  const currentStyle = getColorParams(currentStatus);

  useEffect(() => {
    const handleClose = () => setIsOpen(false);
    if (isOpen) {
      window.addEventListener("click", handleClose);
      window.addEventListener("scroll", handleClose, true);
      document.addEventListener("close-dropdowns", handleClose);
    }
    return () => {
      window.removeEventListener("click", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      document.removeEventListener("close-dropdowns", handleClose);
    };
  }, [isOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (isInert) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    document.dispatchEvent(new CustomEvent("close-dropdowns"));

    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      // Wide enough for "Move to another team", which is the longest line.
      const width = Math.max(rect.width, 210);

      // Near the right edge a left-anchored menu overflows and gets clipped —
      // right-align it to the trigger then, and never let it slip past either
      // viewport edge.
      let left = rect.left;
      if (left + width > viewportWidth - 8) left = rect.right - width;
      if (left < 8) left = 8;

      // --- NEW: Collision Detection Logic ---
      const spaceBelow = viewportHeight - rect.bottom;
      const estimatedDropdownHeight = 170; // Approx height of the 4 options

      /* Placement only — the surface itself (background, radius, z-index,
         border) belongs to PortalDropdown, so the two cannot drift apart. */
      let dynamicStyle = {
        left: `${left}px`,
        width: `${width}px`,
      };

      if (spaceBelow < estimatedDropdownHeight) {
        // Not enough space below, open UPWARDS
        dynamicStyle.bottom = `${viewportHeight - rect.top + 5}px`;
      } else {
        // Plenty of space, open DOWNWARDS
        dynamicStyle.top = `${rect.bottom + 5}px`;
      }

      setMenuStyle(dynamicStyle);
      setIsOpen(true);
    }
  };

  // ... (The rest of the StatusSelect return statement remains exactly the same)

  if (isInert) {
    return (
      <div
        className="d-flex align-items-center gap-2"
        style={{
          backgroundColor: currentStyle.bg,
          color: currentStyle.text,
          padding: "6px 14px",
          borderRadius: "20px",
          fontSize: "0.75rem",
          fontWeight: "600",
          width: "fit-content",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: currentStyle.dot,
          }}
        ></div>
        <span>{displayStatus(currentStatus) || "Testing"}</span>
      </div>
    );
  }
  return (
    <>
      <div ref={wrapperRef} className="position-relative d-inline-block">
        <button
          type="button"
          onClick={toggleMenu}
          className="d-flex align-items-center gap-2 shadow-sm border-0"
          style={{
            backgroundColor: currentStyle.bg,
            color: currentStyle.text,
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.75rem",
            fontWeight: "600",
            cursor: "pointer",
            minWidth: "125px",
            justifyContent: "space-between",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: currentStyle.dot,
              }}
            ></div>
            <span>{displayStatus(currentStatus) || "Testing"}</span>
          </div>
          <i
            className="bi bi-chevron-down"
            style={{ fontSize: "0.6rem", opacity: 0.7 }}
          ></i>
        </button>
      </div>
      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        style={menuStyle}
      >
        <div className="p-1 overflow-auto" style={{ maxHeight: "300px" }}>
          {(readOnly
            ? []
            : [
                "Testing In Progress",
                "Testing On Hold",
                "Update",
                "Complete",
                "Reassign",
              ]
          ).map((opt) => {
            const optStyle = getColorParams(opt);
            const isSelected = opt === currentStatus;

            // --- MUST START WITH TESTING IN PROGRESS CONDITION ---
            let isDisabled = false;
            // Rule 1: New tasks MUST start with "Testing In Progress"
            if ((currentStatus === "Testing" || !currentStatus) && opt !== "Testing In Progress") {
              isDisabled = true;
            }

            // Rule 2: If "Testing On Hold", only allow moving to "Testing In Progress"
            if (currentStatus === "Testing On Hold" && opt !== "Testing In Progress") {
              isDisabled = true;
            }

            // ⭐ CRITICAL FIX: Never disable the option that is currently selected
            // This prevents "Testing On Hold" from getting the lock icon when you open the menu!
            if (opt === currentStatus) {
              isDisabled = false;
            }
            return (
              <div
                key={opt}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSelected && !isDisabled) {
                    onChange(opt);
                    setIsOpen(false);
                  }
                }}
                className="d-flex align-items-center px-3 py-2 rounded-2 my-1"
                style={{
                  cursor: isDisabled || isSelected ? "default" : "pointer",
                  fontSize: "0.8rem",
                  color: isDisabled ? "#adb5bd" : "#495057",
                  backgroundColor: isSelected ? "#f8f9fa" : "transparent",
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: optStyle.dot,
                    marginRight: "10px",
                  }}
                ></div>
                <span className="flex-grow-1">{opt}</span>
                {isDisabled && <i className="bi bi-lock-fill small"></i>}
              </div>
            );
          })}

          {canMove && (
            <>
              {/* Below the rule is a different kind of action: the ones above
                  change this task's state, this one gives it away. */}
              {!readOnly && (
                <div
                  style={{
                    borderTop: "1px solid #eef1f6",
                    margin: "6px 10px",
                  }}
                />
              )}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onMove?.();
                }}
                title={moveHint}
                className="d-flex align-items-center px-3 py-2 rounded-2 my-1"
                /* Hover inline rather than via .dropdown-item-hover: that
                   class lives in developmentTask.css, which this page does
                   not import. */
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#eef2ff")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
                style={{
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#4f46e5",
                  transition: "background-color 0.15s ease",
                }}
              >
                <i
                  className="bi bi-arrow-left-right"
                  style={{ marginRight: "10px" }}
                  aria-hidden="true"
                />
                <span className="flex-grow-1">Move to another team</span>
              </div>
            </>
          )}
        </div>
      </PortalDropdown>
    </>
  );
};

const DescriptionCell = ({ value, onEdit, readOnly }) => {
  const truncateText = (str) =>
    str && str.length > 20 ? str.substring(0, 20) + "..." : str;
  if (readOnly)
    return (
      <span className="text-muted small" title={value}>
        {truncateText(value) || "-"}
      </span>
    );
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
      className="text-truncate text-muted d-flex align-items-center"
      title={value}
      style={{ cursor: "pointer", maxWidth: "200px", fontSize: "0.85rem" }}
    >
      <span className="me-2">
        {value ? truncateText(value) : "Add Description..."}
      </span>
      <i
        className="bi bi-pencil text-primary"
        style={{ fontSize: "0.7rem", opacity: 0.7 }}
      ></i>
    </div>
  );
};

const TaskDetailContent = ({ task }) => {
  if (!task) return null;
  const DetailRow = ({
    label,
    value,
    fullWidth = false,
    isBadge = false,
    badgeColor = "secondary",
  }) => (
    <div className={`mb-3 ${fullWidth ? "col-12" : "col-md-4 col-sm-6"}`}>
      <div
        className="text-muted small text-uppercase fw-bold mb-1"
        style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
      >
        {label}
      </div>
      {isBadge ? (
        <span
          className={`badge bg-${badgeColor} text-${badgeColor === "warning" || badgeColor === "info" ? "dark" : "white"}`}
        >
          {value}
        </span>
      ) : (
        <div
          className="fw-medium text-dark"
          style={{ fontSize: "0.9rem", wordBreak: "break-word" }}
        >
          {value || "-"}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-start mb-3 pb-3 border-bottom">
        <div>
          <h5 className="fw-bold text-primary mb-1">{task.TaskName}</h5>
          <div className="text-muted small">{task.NatureofQuery}</div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1">
          <span className="badge bg-light text-secondary border">
            Query #{task.QueryNo}
          </span>
          <small className="text-muted">
            {formatDateTimeDisplay(task.QueryDate)}
          </small>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-12">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Client Information
          </h6>
        </div>
        <DetailRow
          label="Client / Company"
          value={task.ClientName || task.CompanyName}
          fullWidth
        />
        <DetailRow label="Project Name" value={task.ProjectName} />
        <DetailRow label="User Name" value={task.Client_UserName} />
        <DetailRow label="Mobile No" value={task.MobileNo} />

        <div className="col-12 mt-2">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Task Settings
          </h6>
        </div>
        <DetailRow
          label="Status"
          value={displayStatus(task.Status)}
          isBadge
          badgeColor={task.Status === "Complete" ? "success" : "primary"}
        />
        <DetailRow
          label="Priority"
          value={task.Priority}
          isBadge
          badgeColor={task.Priority === "High" ? "danger" : "info"}
        />
        <DetailRow
          label="Assigned Time"
          value={
            formatDateTimeDisplay(task.AssignedTime)
          }
        />

        <div className="col-12 mt-2">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Timing
          </h6>
        </div>
        <DetailRow
          label="Est. Time"
          value={task.EstimateTime ? `${task.EstimateTime} mins` : "-"}
        />
        <DetailRow
          label="Start Time"
          value={
            formatDateTimeDisplay(task.StartTime)
          }
        />
        <DetailRow
          label="End Time"
          value={
            formatDateTimeDisplay(task.EndTime)
          }
        />

        <div className="col-12 mt-2">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Content
          </h6>
        </div>
        <DetailRow label="Description" value={task.Description} fullWidth />

        {/* Same block as the other task dialogs, read-only: the screenshot is
            part of the description, and it should not matter which screen you
            opened the task from. */}
        {task.QueryCode && (
          <div className="col-12 mt-2">
            <TaskImages queryCode={task.QueryCode} readOnly />
          </div>
        )}
        <DetailRow label="Solution" value={task.Solution} fullWidth />
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// MAIN COMPONENT
// --------------------------------------------------------------------------

const TestingTask = () => {
  const dispatch = useDispatch();
  const [localTasks, setLocalTasks] = useState([]);

  // --- CLIENT COUNT & FILTER LOGIC ---
  const [selectedClientFilter, setSelectedClientFilter] = useState("All");

  const uniqueClients = useMemo(() => {
    const clients = localTasks.map(
      (t) => t.ClientName || t.CompanyName || "Unknown",
    );
    return [...new Set(clients)].filter(Boolean);
  }, [localTasks]);

  const filteredTasks = useMemo(() => {
    if (selectedClientFilter === "All") return localTasks;
    return localTasks.filter(
      (t) =>
        (t.ClientName || t.CompanyName || "Unknown") === selectedClientFilter,
    );
  }, [localTasks, selectedClientFilter]);

  const displayTaskCount = filteredTasks.length;

  // --- MODAL & ACTION STATES ---
  const [editDescTask, setEditDescTask] = useState(null);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descText, setDescText] = useState("");
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [taskToMove, setTaskToMove] = useState(null);

  const tasksData = useSelector(selectTestingLists);
  const listStatus = useSelector(selectTestingListsStatus);

  const fetchTasks = () => {
    dispatch(testingListsApis());
  };

  /* Coming back to this tab re-asks for the list. Nothing to force here —
     this fetch takes no parameters and has no de-dupe to get past. */
  useTabRefresh(fetchTasks);

  // Runs once on mount (StrictMode-safe) so the list isn't fetched twice.
  useEffectOnce(() => {
    fetchTasks();
  });

  useEffect(() => {
    const rawData = tasksData?.data;
    const list = Array.isArray(rawData) ? rawData : rawData?.data || [];
    if (list) {
      setLocalTasks(
        list.map((task) => ({
          ...task,
          totalTimeMs: Number(task?.totalTimeMs) || 0,
          currentSessionStart: task?.currentSessionStart || null,
          Status: task?.Status || "Testing",
        })),
      );
    }
  }, [tasksData]);

  const handleExport = (data) => alert(`Exporting ${data.length} tasks...`);

  // --- BACKEND SYNC ---
  const syncWithBackend = (task, type) => {
    setLoading(true);
    let payload = {
      queryCode: task.QueryCode,
      isUpdate: task.isUpdate,
      description: task.Description || "",
    };

    if (task?.Status === "Testing In Progress") {
      payload.startTime = formatDateForPayload(task.StartTime);
      payload.status = task.Status;
    } else if (task?.Status === "Complete") {
      payload.endTime = formatDateForPayload(task.EndTime);
      payload.status = "Complete";
      payload.startTime = formatDateForPayload(task.StartTime);
      payload.timeTaken = task.TotalTimeTaken_Seconds;
    } else {
      payload.status = task?.Status;
    }

    const statusTrackerPayload = {
      queryCode: task.QueryCode,
      status: task?.Status,
    };

    if (type === "DESCRIPTION" && task.Status !== "Complete") {
      dispatch(updateDevelopmentTasksApis(payload)).then(() => {
        setLoading(false);
        fetchTasks();
      });
    } else {
      dispatch(createStatusTrackerApis(statusTrackerPayload)).then((value) => {
        if (value?.payload?.success) {
          if (task?.Status === "Complete") {
            payload.timeTaken =
              value?.payload?.data?.[0]?.TotalTimeTaken_Seconds;
          }
          dispatch(updateDevelopmentTasksApis(payload)).then(() => {
            setLoading(false);
            fetchTasks();
          });
        } else {
          setLoading(false);
        }
      });
    }
  };

  // --- HANDLE STATUS CHANGE ---
  const handleStatusChange = (rowId, newStatus) => {
    const taskIndex = localTasks.findIndex((t) => t.QueryNo === rowId);
    if (taskIndex === -1) return;
    const task = localTasks[taskIndex];

    const currentIsUpdate = task.IsUpdate || task.isUpdate;
    if (
      newStatus !== "Testing" &&
      newStatus !== "Testing In Progress" &&
      (!currentIsUpdate ||
        currentIsUpdate === "None" ||
        currentIsUpdate === "null")
    ) {
      toast.warning(
        "Please select a valid 'Is Update' option before updating the status.",
      );
      return;
    }

    if (
      (task.Status === "Testing" || !task.Status) &&
      newStatus !== "Testing In Progress"
    ) {
      toast.warning(
        "You must select 'Testing In Progress' before moving to other statuses.",
      );
      return;
    }

    if (
      newStatus === "Reassign" ||
      (newStatus === "Complete" &&
        (!task.Description || task.Description.trim() === ""))
    ) {
      if (newStatus === "Reassign") {
        toast.info("Please provide a reason for reassigning.");
      } else {
        toast.warning("Description is required to complete the task.");
      }

      setPendingStatusUpdate({ rowId, newStatus });
      openDescriptionModal(task);
      return;
    }

    const now = new Date();
    let updates = { Status: newStatus };

    if (newStatus === "Testing In Progress" && !task.StartTime)
      updates.StartTime = now.toISOString();
    if (newStatus === "Complete") updates.EndTime = now.toISOString();

    const updatedTask = { ...task, ...updates };

    setLocalTasks((prev) =>
      prev.map((t) => (t.QueryNo === rowId ? updatedTask : t)),
    );
    syncWithBackend(updatedTask);
  };

  const handleIsUpdateChange = async (task, newValue) => {
    setLoading(true);
    setLocalTasks((prev) =>
      prev.map((t) =>
        t.QueryNo === task.QueryNo
          ? { ...t, IsUpdate: newValue, isUpdate: newValue }
          : t,
      ),
    );

    const payload = {
      queryCode: task.QueryCode || task.QueryNo,
      isUpdate: newValue,
      status: task.Status || "Testing",
    };

    try {
      const res = await dispatch(updateDevelopmentTasksApis(payload));
      if (res?.payload?.success) {
        toast.success("Is Update status changed!");
        fetchTasks();
      } else {
        toast.error("Failed to update status");
        fetchTasks();
      }
    } catch (error) {
      toast.error("Failed to update status");
      fetchTasks();
    } finally {
      setLoading(false);
    }
  };

  const openDescriptionModal = (task) => {
    setEditDescTask(task);
    setDescText(task.Description || "");
    setShowDescModal(true);
  };

  const handleSaveDescription = () => {
    if (!editDescTask) return;

    if (!isValidText(descText)) {
      toast.error(
        "Please enter a valid complete reason in a correct sentence.",
      );
      return;
    }

    const now = new Date();
    let updates = { Description: descText.trim() };

    if (
      pendingStatusUpdate &&
      pendingStatusUpdate.rowId === editDescTask.QueryNo
    ) {
      updates.Status = pendingStatusUpdate.newStatus;
      if (updates.Status === "Complete") updates.EndTime = now.toISOString();
      if (updates.Status === "Testing In Progress" && !editDescTask.StartTime)
        updates.StartTime = now.toISOString();
    }

    const updatedTask = { ...editDescTask, ...updates };

    setLocalTasks((prev) =>
      prev.map((t) => (t.QueryNo === updatedTask.QueryNo ? updatedTask : t)),
    );

    if (pendingStatusUpdate) {
      syncWithBackend(updatedTask);
    } else {
      syncWithBackend(updatedTask, "DESCRIPTION");
    }

    setShowDescModal(false);
    setPendingStatusUpdate(null);
  };

  const columns = useMemo(() => {
    return [
      {
        /* Was key: "UserName" — a column labelled Client Name, filtering on a
           field the rows do not carry. Same fix as DevelopmentTask: the key
           names what is shown, and searchValue/sortValue spell out the same
           CompanyName fallback the cell renders. */
        label: "Client Name",
        key: "ClientName",
        searchValue: (r) => r.ClientName || r.CompanyName || "",
        sortValue: (r) => r.ClientName || r.CompanyName || "",
        width: "200px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div
            style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          >
            {r.ClientName || r.CompanyName || "-"}
          </div>
        ),
      },
      {
        label: "Form Name",
        key: "FormName",
        /* The cell falls back to MainMenuName, so the filter has to too. */
        searchValue: (r) => r.FormName || r.MainMenuName || "",
        sortValue: (r) => r.FormName || r.MainMenuName || "",
        width: "180px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div
            style={{
              wordBreak: "break-all",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          >
            {r.FormName || r.MainMenuName || "-"}
          </div>
        ),
      },
      {
        label: "Task Name",
        key: "TaskName",
        /* A task with no TaskName shows its NatureofQuery; searching the words
           on screen has to find it. */
        searchValue: (r) => r.TaskName || r.NatureofQuery || "",
        sortValue: (r) => r.TaskName || r.NatureofQuery || "",
        width: "250px",
        cellClass: "text-cell-wrap",
        render: (r) => (
          <div
            onClick={() => {
              setSelectedTask(r);
              setShowDetailModal(true);
            }}
            className="text-primary fw-medium cursor-pointer"
            style={{
              cursor: "pointer",
              wordBreak: "break-word",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          >
            {r.TaskName || r.NatureofQuery}
          </div>
        ),
      },
      {
        label: "Query Type",
        key: "QueryType",
        width: "150px",
        render: (r) => {
          let badgeClass = "bg-secondary";
          const type = r.QueryType ? r.QueryType.trim() : "";
          if (type === "Bug" || type === "BUG") badgeClass = "bg-danger";
          else if (type === "New Task") badgeClass = "bg-primary";
          else if (type === "Rework") badgeClass = "bg-warning text-dark";

          return (
            <span
              className={`badge ${badgeClass} rounded-pill`}
              style={{ padding: "6px 12px", letterSpacing: "0.5px" }}
            >
              {type || "-"}
            </span>
          );
        },
      },
      {
        label: "Start Time",
        key: "StartTime",
        type: "datetime",
        width: "140px",
        render: (r) => (
          <span className="text-muted small">
            {formatDateTimeDisplay(r.StartTime) || "-"}
          </span>
        ),
      },
      {
        label: "Is Update",
        key: "IsUpdate",
        width: "140px",
        type: "select",
        render: (r) => (
          <IsUpdateSelect
            initialValue={r.IsUpdate || r.isUpdate}
            onChange={(val) => handleIsUpdateChange(r, val)}
            readOnly={r.Status === "Complete"}
          />
        ),
      },
      {
        label: "Description",
        key: "Description",
        width: "200px",
        render: (r) => (
          <DescriptionCell
            value={r.Description}
            onEdit={() => openDescriptionModal(r)}
            readOnly={r.Status === "Complete"}
          />
        ),
      },
      {
        label: "Status",
        key: "Status",
        width: "170px",
        render: (r) => (
          <StatusSelect
            currentStatus={r.Status}
            readOnly={r.Status === "Complete"}
            onChange={(val) => handleStatusChange(r.QueryNo, val)}
            /* QC is where work most often goes backwards. Failing a task has
               to send it to whoever can fix it, with the reason attached — the
               move dialog makes that one action instead of a status change
               plus a message somewhere else. It sits in this dropdown rather
               than in a column of its own; see StatusSelect. */
            canMove={Boolean(r.TeamCode)}
            moveHint="Send this task to another team"
            onMove={() => {
              setTaskToMove(r);
              setShowMoveModal(true);
            }}
          />
        ),
      },
      {
        label: "Team",
        key: "TeamName",
        width: "110px",
        type: "select",
        filterType: "select",
        searchValue: (r) => r.TeamName || "",
        render: (r) => <TeamBadge name={r.TeamName} short={r.TeamShortName} />,
      },
    ];
  }, [localTasks]);

  return (
    <div className="report-container">
      {/* ----------------------------------------------------
          MODERN HEADER WITH THE NEW DROPDOWN COMPONENT
      -------------------------------------------------------*/}
      <PageHeader
      title={
        <div className="d-flex align-items-center text-nowrap">

          <i className="bi bi-shield-check me-2 text-primary"></i>
          <span>Quality Assurance</span>
        </div>
      }
      subtitle="Review and Monitor quality Assurance Activities"
      actions={
        <ClientFilterDropdown
          value={selectedClientFilter}
          options={uniqueClients}
          onChange={setSelectedClientFilter}
          count={displayTaskCount}
        />
      }
/>

      <div style={{ position: "relative", minHeight: "200px" }}>
        {(listStatus === "loading" || loading) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              zIndex: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader isLoading={true} />
          </div>
        )}

        <ReportTable
          data={filteredTasks}
          columns={columns}
          onExport={handleExport}
          stickyColumns={["UserName"]}
        />
      </div>

      <Modal
        show={showDescModal}
        onClose={() => {
          setShowDescModal(false);
          setPendingStatusUpdate(null);
        }}
        title="Edit Description"
        maxWidth="500px"
      >
        <div className="p-3">
          <textarea
            className="form-control mb-3"
            rows="5"
            value={descText}
            onChange={(e) => setDescText(e.target.value)}
            autoFocus
          />
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-sm btn-light border"
              onClick={() => {
                setShowDescModal(false);
                setPendingStatusUpdate(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSaveDescription}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Task Details"
        maxWidth="900px"
      >
        {selectedTask && <TaskDetailContent task={selectedTask} />}
      </Modal>

      <MoveTaskModal
        show={showMoveModal}
        task={taskToMove}
        onClose={() => {
          setShowMoveModal(false);
          setTaskToMove(null);
        }}
        onMoved={() => {
          // Once it leaves QC it is no longer a testing task, so the list has
          // to be re-read rather than patched in place.
          fetchTasks();
        }}
      />
    </div>
  );
};

export default TestingTask;
